// @flow

import styled from '@emotion/styled'
import { Link } from 'gatsby'
import { GatsbyImage, StaticImage } from 'gatsby-plugin-image'
import * as React from 'react'

import { colors, media } from '@toggl/style'
import { Button, Responsive, text } from '@toggl/ui'

type Props = {|
  className?: string,
  quote: {
    text: string,
    description: string,
    link?: string,
    image:
      | React.Element<typeof GatsbyImage>
      | React.Element<typeof StaticImage>
      | React.Element<'img'>,
  },
  persuader: {
    text: string,
    cta?: string,
    link?: string,
  },
  logo: {
    image:
      | React.Element<typeof GatsbyImage>
      | React.Element<typeof StaticImage>
      | React.Element<'img'>,
    mobileImage:
      | React.Element<typeof GatsbyImage>
      | React.Element<typeof StaticImage>
      | React.Element<'img'>,
  },
|}

export default function QuoteLogos({
  className,
  quote,
  persuader,
  logo,
}: Props) {
  return (
    <Root className={className}>
      <Wrapper>
        <QuoteStyled>
          <TextSection>
            <text.H4 color={colors.darkPurple}>{quote.text}</text.H4>
            <text.P3 color={colors.fadedPurple}>
              &#x2014; {quote.description}
            </text.P3>

            {quote.link && (
              <Link to={quote.link}>
                <Button.Secondary
                  tabIndex="-1"
                  color={colors.darkPurple}
                  caretDirection="right"
                >
                  Read the case study
                </Button.Secondary>
              </Link>
            )}
          </TextSection>
          <Responsive.DesktopOnly>{quote.image}</Responsive.DesktopOnly>
        </QuoteStyled>

        <CTA>
          <text.H4 color={colors.darkPurple}>{persuader.text}</text.H4>

          {persuader.link ? (
            <Link to={persuader.link}>
              <Button.Primary tabIndex="-1">{persuader.cta}</Button.Primary>
            </Link>
          ) : (
            <Link to="/track/signup/">
              <Button.Primary tabIndex="-1">Try Toggl Track</Button.Primary>
            </Link>
          )}
        </CTA>

        <Responsive.DesktopOnly>{logo.image}</Responsive.DesktopOnly>
        <Responsive.MobileOnly>{logo.mobileImage}</Responsive.MobileOnly>
      </Wrapper>
    </Root>
  )
}

const QuoteStyled: React.ComponentType<any> = styled.div`
  background: ${colors.almostWhite};
  padding: 40px;
  display: flex;
  column-gap: 40px;
  .gatsby-image-wrapper {
    margin-top: -35%;
  }
`

const TextSection = styled.div`
  flex: 1;
  p {
    margin: 30px 10px 0 0;
    display: inline-block;
  }
  button {
    display: inline-block;
  }
`

const Root: React.ComponentType<any> = styled.div`
  padding: 40px 0;
  ${media.mq[2]} {
    padding: 80px 0;
  }
`

const Wrapper = styled.div`
  max-width: 1000px;
  margin: auto;
  position: relative;
  ${media.mq[2]} {
    &:before,
    &:after {
      position: absolute;
      height: 40px;
    }
    &:after {
      content: url(${require('./assets/shape-eclipse.svg')});
      right: -5%;
      bottom: 30%;
    }
    &:before {
      content: url(${require('./assets/shape-multi.svg')});
      left: -90px;
      top: -80px;
    }
  }
`

const CTA = styled.div`
  background: ${colors.beige};
  padding: 30px 40px;
  margin-bottom: 60px;
  h4 {
    margin-bottom: 15px;
  }
  ${media.mq[1]} {
    display: flex;
    align-items: center;
    column-gap: 25px;
    padding: 20px 30px 20px 40px;
    h4 {
      flex: 1;
      margin: 0;
    }
  }
  ~ * {
    text-align: center;
    padding: 0 15px;
  }
`
